// Send fleet "When" (G-59, P32): leave now, or arrive at a local clock time. The time is chosen with
// three dropdowns — day, hour, minute in 5-minute steps (Tim, 2026-10-13) — that only list arrivals
// inside the valid window: from the earliest arrival to leaving within `maxAheadH` (G-68). An
// arrival that leaves the window (time passes, other ships change the route) moves to its edge with
// a note. The server checks the same window (TOO_LATE / TOO_FAR_AHEAD).
import { useEffect, useMemo, useState } from 'react';
import {
  arrivalSlots,
  dayKey,
  departureFor,
  earliestArrival,
  latestArrival,
  pickSlot,
  quickArrivals,
  slotDays,
  slotHours,
  slotMinutes,
  snapToSlot,
} from '../../fleetTiming';
import { formatDuration } from '../../labels';
import { Button, SegmentedControl, Select } from '../ui';

const clock = (ms: number) =>
  new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const pad = (n: number) => String(n).padStart(2, '0');

export type WhenMode = 'now' | 'at';

export function WhenPicker({
  mode,
  onMode,
  arriveAt,
  onArriveAt,
  totalS,
  maxAheadH,
  now,
}: {
  mode: WhenMode;
  onMode: (mode: WhenMode) => void;
  arriveAt: number | null;
  onArriveAt: (ms: number | null) => void;
  /** Route time in seconds; null until the route is known. */
  totalS: number | null;
  /** `travel.timed_dispatch_max_ahead_h` from the codex; null until it is loaded. */
  maxAheadH: number | null;
  now: number;
}) {
  const [adjusted, setAdjusted] = useState<'early' | 'late' | null>(null);
  const earliest = totalS === null ? null : earliestArrival(now, totalS);
  const latest =
    totalS === null || maxAheadH === null ? null : latestArrival(now, totalS, maxAheadH);
  // The window only changes when a bound moves (to the next minute, or with a new route).
  const slots = useMemo(
    () => (earliest === null || latest === null ? [] : arrivalSlots(earliest, latest)),
    [earliest, latest]
  );

  // Keep the chosen arrival on a slot: start at the earliest, follow the window as it moves.
  useEffect(() => {
    if (mode !== 'at' || slots.length === 0) return;
    if (arriveAt === null) {
      onArriveAt(slots[0] ?? null);
      return;
    }
    if (slots.includes(arriveAt)) return;
    const snapped = snapToSlot(arriveAt, slots);
    if (snapped.adjusted) setAdjusted(snapped.adjusted);
    onArriveAt(snapped.at);
  }, [mode, slots, arriveAt, onArriveAt]);

  const choose = (at: number | null) => {
    setAdjusted(null);
    if (at !== null) onArriveAt(at);
  };
  const chosen = arriveAt !== null && slots.includes(arriveAt) ? arriveAt : null;
  const day = chosen === null ? '' : dayKey(chosen);
  const hour = chosen === null ? -1 : new Date(chosen).getHours();
  const minute = chosen === null ? -1 : new Date(chosen).getMinutes();
  const depart = chosen !== null && totalS !== null ? departureFor(chosen, totalS) : null;

  return (
    <div className="space-y-2">
      <SegmentedControl
        label="When"
        value={mode}
        options={[
          { value: 'now', label: 'Now' },
          { value: 'at', label: 'Arrive at' },
        ]}
        onChange={onMode}
      />
      {mode === 'at' ? (
        slots.length === 0 ? (
          <p className="text-sm text-muted">Choose ships and a target to see the arrival times.</p>
        ) : (
          <>
            <div className="grid grid-cols-[2fr_1fr_1fr] gap-2">
              <Select
                label="Arrival day"
                value={day}
                onChange={(e) => choose(pickSlot(slots, e.target.value, hour, minute))}
              >
                {slotDays(slots, now).map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label}
                  </option>
                ))}
              </Select>
              <Select
                label="Hour"
                value={String(hour)}
                onChange={(e) => choose(pickSlot(slots, day, Number(e.target.value), minute))}
              >
                {slotHours(slots, day).map((h) => (
                  <option key={h} value={String(h)}>
                    {pad(h)}
                  </option>
                ))}
              </Select>
              <Select
                label="Minute"
                value={String(minute)}
                onChange={(e) => choose(pickSlot(slots, day, hour, Number(e.target.value)))}
              >
                {slotMinutes(slots, day, hour).map((m) => (
                  <option key={m} value={String(m)}>
                    {pad(m)}
                  </option>
                ))}
              </Select>
            </div>
            {earliest !== null ? (
              <div className="flex gap-2">
                {quickArrivals(now, earliest, latest ?? undefined).map((q) => (
                  <Button
                    key={q.label}
                    variant="secondary"
                    disabled={q.disabled}
                    onClick={() => choose(q.at)}
                  >
                    {q.label}
                  </Button>
                ))}
              </div>
            ) : null}
            {adjusted ? (
              <p data-arrival-adjusted className="text-xs text-accent">
                {adjusted === 'early'
                  ? 'Moved to the earliest possible arrival.'
                  : `Moved to the latest possible arrival — a timed fleet must leave within ${maxAheadH} h.`}
              </p>
            ) : null}
            {depart !== null && chosen !== null ? (
              <p data-departure className="text-sm text-muted">
                Departs at {clock(depart)} (in {formatDuration(Math.max(0, depart - now))}) ·
                arrives {clock(chosen)}
              </p>
            ) : null}
          </>
        )
      ) : null}
    </div>
  );
}
